import type { Field } from '@formily/core';
import { useField } from '@formily/react';
import { observer } from '@formily/reactive-react';
import type { IProps as IUploadFileProps } from '../../../components/UploadFile';
import type { IUploadFileResponse } from '../../../hooks/useUploadFile';
import { IFormFieldSchema } from '../../types';
import UploadFile from '../../../components/UploadFile';

export interface IFormFilesUploadProps extends IUploadFileProps {
  visible?: boolean;
}

export interface IProps {
  schema?: IFormFieldSchema<IFormFilesUploadProps>;
}

const fileUploadedTransformer = (file: IUploadFileResponse) => {
  return {
    id: file.id,
    disk: file.disk,
    bucket: file.bucket,
    path: file.path,
    name: file.name,
    size: file.size,
    extension: file.extension,
  };
};

const FormFilesUpload = observer(({ schema }: IProps = {}) => {
  const field = useField<Field>();

  const componentProps = schema
    ? ((schema['x-component-props'] ?? {}) as IFormFilesUploadProps)
    : ((field.componentProps ?? {}) as IFormFilesUploadProps);

  const { visible, multiple, disabled, accept, ...rest } = componentProps;

  if (visible === false) return null;

  const handleUploadSuccess = (uploadedFile: IUploadFileResponse) => {
    if (multiple) {
      const currentFiles = (field.value as IUploadFileResponse[]) || [];
      field.setValue([...currentFiles, fileUploadedTransformer(uploadedFile)]);
    } else {
      field.setValue(fileUploadedTransformer(uploadedFile));
    }
  };

  const files = field?.value
    ? Array.isArray(field?.value)
      ? field?.value
      : [field?.value]
    : [];

  const acceptFiles = Array.isArray(accept) ? accept.join(',') : accept;

  return (
    <UploadFile
      {...rest}
      disabled={field?.disabled || disabled}
      multiple={multiple}
      onUploadSuccess={handleUploadSuccess}
      files={files}
      accept={acceptFiles}
    />
  );
});

export default FormFilesUpload;
